import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import impersonationService from "../services/impersonationService";
import { setCredentials, logout } from "../store/slices/authSlice";
import {
  impersonationStarted,
  impersonationEnded,
} from "../store/slices/impersonationSlice";
import {
  stashImpersonationOrigin,
  readImpersonationOrigin,
  clearImpersonationOrigin,
  rememberRecentTarget,
} from "../utils/impersonationStorage";

// Start/exit flows for the "View as" role preview (TP-View-As).
//
// The whole session lives in state.auth (mirrored to localStorage
// tokens/user), so ONE setCredentials dispatch swaps the entire app to the
// target: PrivateRoute, menus and the inbox model re-evaluate from
// state.auth, and both Stream clients (useInbox / useGlobalChatUnread) are
// keyed on the userId and disconnect+reconnect on their own — no extra
// Stream code here.
const useImpersonation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  // Refuses a second start() on this hook instance while a start POST is
  // still in flight (e.g. rapid double-click on two Recent rows).
  const startInFlight = useRef(false);

  // target = a picker row ({ account_id, name, email, role_label, ... }).
  const start = useCallback(
    async (target) => {
      // No nested previews: a second start while one is active would stash
      // target #1 as "origin" and orphan the superuser's own session. The
      // control is hidden while previewing, but a preview target that is
      // itself a superuser would see it again — hard-refuse here.
      if (readImpersonationOrigin()) {
        throw new Error(
          "A View-as session is already active — exit it before starting a new one.",
        );
      }
      if (startInFlight.current) {
        throw new Error("A View-as start is already in progress.");
      }
      startInFlight.current = true;
      try {
        const res = await impersonationService.start(target.account_id);
        if (!res?.success || !res?.user || !res?.tokens?.access) {
          throw new Error("Impersonation start failed");
        }

        // Stash-if-absent, re-checked AFTER the await: an overlapping start
        // (other hook instance / other tab) may have swapped meanwhile — the
        // first preview wins, this one must not overwrite its origin.
        if (readImpersonationOrigin()) {
          throw new Error(
            "A View-as session is already active — exit it before starting a new one.",
          );
        }

        // Stash the superuser's session BEFORE the swap, under a separate key
        // that setCredentials never touches. path = where the preview started,
        // so exit returns to this exact page. Read tokens/user fresh from
        // localStorage: the interceptor may have rotated them during the start
        // POST, and a stale stash would restore revoked credentials on exit.
        let storedUser = null;
        let storedTokens = null;
        try {
          storedUser = JSON.parse(localStorage.getItem("user"));
          storedTokens = JSON.parse(localStorage.getItem("tokens"));
        } catch (e) {
          // Fall back to the Redux snapshot below.
        }
        stashImpersonationOrigin({
          user: storedUser || auth.user,
          tokens: storedTokens || auth.tokens,
          path: location.pathname,
        });
        rememberRecentTarget(target);

        // The start payload mirrors the login payload; normalize the
        // snake_case stream/kyc keys onto the app's user shape (login-mapping
        // parity — `steamVideoId` is the app-wide key, typo and all).
        const u = res.user;
        const targetUser = {
          ...u,
          streamChatId: u.streamChatId ?? u.stream_chat_id,
          steamVideoId: u.steamVideoId ?? u.stream_video_token,
          kycVerified: u.kycVerified ?? u.kyc_verified,
          is_superuser: u.is_superuser === true,
        };

        dispatch(
          impersonationStarted({
            name: targetUser.name || target.name || target.email,
            role:
              targetUser.currentRole ||
              (targetUser.groups || targetUser.roles || [])[0] ||
              target.role_label ||
              "",
          }),
        );
        dispatch(setCredentials({ user: targetUser, tokens: res.tokens }));
        // Deterministic landing as the target; PrivateRoute would bounce
        // role-restricted pages to "/" anyway.
        navigate("/");
      } finally {
        startInFlight.current = false;
      }
    },
    [auth.user, auth.tokens, dispatch, location.pathname, navigate],
  );

  const exit = useCallback(async () => {
    const origin = readImpersonationOrigin();
    try {
      // Must run while the TARGET's JWT is still active — the imp claim on it
      // identifies the superuser, closes the audit row and revokes the
      // preview refresh token server-side.
      await impersonationService.exit();
    } catch (e) {
      // Still restore locally — a failed exit call must never strand the
      // superuser inside the preview.
      console.error("Impersonation exit call failed:", e);
    }

    if (origin?.user && origin?.tokens) {
      dispatch(setCredentials({ user: origin.user, tokens: origin.tokens }));
      clearImpersonationOrigin();
      dispatch(impersonationEnded());
      navigate(origin.path || "/");
    } else {
      // Origin missing/corrupt: never stay silently logged in AS the target —
      // force a clean logout (also clears the origin key) and re-auth.
      dispatch(impersonationEnded());
      dispatch(logout());
      navigate("/login");
    }
  }, [dispatch, navigate]);

  return { start, exit };
};

export default useImpersonation;
